import { Link } from "@tanstack/react-router";
import { useNavigate } from "@tanstack/react-router";
import * as React from "react";
import { Dispatch, SetStateAction } from "react";
import { socialPost } from "app/router";
import { ReactComponent as ThreeDots } from "assets/img/icons/three-dots.svg";
import {
    Avatar,
    Button,
    Dropdown,
    DropdownContent,
    DropdownItem,
    Tooltip,
} from "components/elements";
import { CopyShareButton } from "components/elements/CopyShareButton";
import { useUserTimezone } from "hooks/useUserTimezone";
import { cn } from "lib/cn";
import { formatISODate } from "lib/formatISODate";
import { ClientPost } from "@compass/shared/types";
import { ProfileTooltip } from "../../profile/ProfileTooltip";

type SocialPostHeaderProps = {
    post: ClientPost;
    canEdit: boolean;
    handleDeletePost: () => void;
    setIsPostEditOpen: Dispatch<SetStateAction<any>>;
    compact?: boolean;
};

export const SocialPostHeader = (props: SocialPostHeaderProps) => {
    const { post, canEdit, handleDeletePost, setIsPostEditOpen, compact } = props;
    const navigate = useNavigate();
    const timeZone = useUserTimezone();

    const author = post.author;
    const isIdentified = author.type === "identified";
    const isLinkDisabled = !isIdentified || !author.isSocialEnabled;
    const avatarUrl =
        author.type === "identified" || author.type === "anonymous-self" ? author.avatarUrl : null;

    // Avatar component wrapped conditionally
    const avatarElement = (
        <Tooltip
            disabled={isLinkDisabled}
            content={
                <div>
                    {isIdentified ? <ProfileTooltip authorId={String(author.id)} /> : null}
                </div>
            }
        >
            <div>
                <Avatar
                    className={`${compact ? "h-8 w-8" : "h-[50px] w-[50px]"} shrink-0 bg-slate-200 text-sm text-slate-400`}
                    photoUrl={avatarUrl}
                    name={author.name}
                />
            </div>
        </Tooltip>
    );

    // For anonymous posts, superusers receive `realName` on the author object;
    // when present, reveal it via hover tooltip (dashed underline hint).
    const anonymousRealName = author.type === "anonymous" ? author.realName : undefined;
    const authorLabel = author.name;
    const isInactive = isIdentified && author.isSocialEnabled === false;
    const authorNameElement = (
        <div
            className={cn("text-black", {
                italic: isInactive,
            })}
        >
            {anonymousRealName ? (
                <Tooltip content={anonymousRealName}>
                    <span className="cursor-help underline decoration-gray-400 decoration-dashed underline-offset-2">
                        {authorLabel}
                    </span>
                </Tooltip>
            ) : (
                authorLabel
            )}
        </div>
    );

    return (
        <div className={`mb-1 flex items-center justify-between ${compact ? "gap-2" : "gap-3"}`}>
            <div className="flex flex-row gap-2">
                {!isIdentified || isLinkDisabled ? (
                    avatarElement
                ) : (
                    <Link
                        to="/profile/$id"
                        target="_blank"
                        params={{ id: `${author.id}` }}
                        className="no-underline hover:no-underline"
                    >
                        {avatarElement}
                    </Link>
                )}

                <div className="flex flex-col">
                    <div className="flex h-6 flex-row items-center text-sm font-semibold ">
                        {!isIdentified || isLinkDisabled ? (
                            authorNameElement
                        ) : (
                            <Link
                                to="/profile/$id"
                                target="_blank"
                                params={{ id: `${author.id}` }}
                                className="no-underline hover:no-underline"
                            >
                                {authorNameElement}
                            </Link>
                        )}
                    </div>
                    <div className="flex cursor-pointer flex-row items-center gap-1 whitespace-nowrap text-xs text-[#424242]">
                        <div className="flex flex-row items-center gap-1 leading-none">
                            <Link
                                to="/social/post/$id"
                                params={{
                                    id: post.id.toString(),
                                }}
                                className="flex cursor-pointer flex-row items-center justify-center gap-1 whitespace-nowrap border-none text-xs text-[#424242] no-underline shadow-none hover:!underline focus:outline-none"
                            >
                                <div>{formatISODate(post.createdAt, timeZone)}</div>
                            </Link>
                            {!compact && (
                                <div>
                                    <CopyShareButton
                                        textToCopy={`${window.location.origin}/social/post/${post.id}`}
                                        className="min-w-max border-none shadow-none"
                                    />
                                </div>
                            )}
                        </div>
                        {post.isUpdated && (
                            <span className="text-xs italic text-stone-400">Edited</span>
                        )}
                    </div>
                </div>
            </div>
            {canEdit && !compact ? (
                <div>
                    <Dropdown
                        className="relative z-20"
                        content={
                            <DropdownContent
                                className="w-auto min-w-[150px] bg-white"
                                align="end"
                                arrow={false}
                                bordered
                            >
                                <DropdownItem
                                    onSelect={() => setIsPostEditOpen(true)}
                                    size="smaller"
                                >
                                    Edit
                                </DropdownItem>
                                <DropdownItem onSelect={handleDeletePost} size="smaller">
                                    Delete
                                </DropdownItem>
                            </DropdownContent>
                        }
                    >
                        <ThreeDots className="cursor-pointer" />
                    </Dropdown>
                </div>
            ) : null}
        </div>
    );
};
